import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import WeatherWidget from '../../components/WeatherWidget';
import IndiaMap from '../../components/IndiaMap';
import AgricultureSummary from '../../components/AgricultureSummary';
import NotificationPopup from '../Notificationpopup';
import Footer from '../../components/Footer';
import '../state/StateDashboard.css';

const DistrictDashboard = () => {
  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  const selectedState = userData.selectedState || 'Unknown State';
  const selectedDistrict = userData.selectedDistrict || 'Unknown District';

  const [districtData, setDistrictData] = useState(null);
  const [groundwaterData, setGroundwaterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agriculturalMetrics, setAgriculturalMetrics] = useState(null);
  const [seasonalData, setSeasonalData] = useState(null);

  // Fetch district data
  useEffect(() => {
    if (selectedState && selectedDistrict) {
      fetchDistrictData(selectedState, selectedDistrict);
    }
  }, [selectedState, selectedDistrict]);

  const fetchDistrictData = async (stateName, districtName) => {
    try {
      setLoading(true);

      // Fetch district-specific data
      const [districtRes, groundwaterRes] = await Promise.allSettled([
        fetch(`${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001")))}/api/districts/${encodeURIComponent(stateName)}`),
        fetch(`${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001")))}/api/groundwater/district/${encodeURIComponent(stateName)}/${encodeURIComponent(districtName)}?year=2024-2025`)
      ]);

      // Process district data
      if (districtRes.status === 'fulfilled' && districtRes.value.ok) {
        const data = await districtRes.value.json();
        const district = data.districts?.find(d => d.district === districtName);
        if (district) {
          setDistrictData(district);

          // Generate agricultural metrics
          setAgriculturalMetrics({
            totalProduction: (district.primaryProd || 0) + (district.secondaryProd || 0),
            primaryCrop: district.primary,
            secondaryCrop: district.secondary,
            primaryProduction: district.primaryProd || 0,
            secondaryProduction: district.secondaryProd || 0,
            irrigationCoverage: Math.floor(Math.random() * 30) + 60,
            organicFarming: Math.floor(Math.random() * 15) + 5,
            soilHealth: ['Good', 'Fair', 'Excellent'][Math.floor(Math.random() * 3)],
            farmersCount: Math.floor(Math.random() * 50000) + 10000,
            cooperatives: Math.floor(Math.random() * 50) + 10,
            marketPrice: Math.floor(Math.random() * 2000) + 3000
          });

          // Generate seasonal data
          const currentMonth = new Date().getMonth();
          const seasons = {
            kharif: { months: [5, 6, 7, 8, 9], name: 'Kharif' },
            rabi: { months: [10, 11, 0, 1, 2], name: 'Rabi' },
            zaid: { months: [3, 4], name: 'Zaid' }
          };

          let currentSeason = 'rabi';
          for (const [key, season] of Object.entries(seasons)) {
            if (season.months.includes(currentMonth)) {
              currentSeason = key;
              break;
            }
          }

          setSeasonalData({
            currentSeason: seasons[currentSeason].name,
            sowingProgress: Math.floor(Math.random() * 40) + 60,
            harvestProgress: currentSeason === 'rabi' ? Math.floor(Math.random() * 30) + 20 : 0,
            expectedYield: Math.floor(Math.random() * 20) + 85,
            weatherSuitability: Math.floor(Math.random() * 30) + 70
          });
        }
      }

      // Process groundwater data
      if (groundwaterRes.status === 'fulfilled' && groundwaterRes.value.ok) {
        const gwData = await groundwaterRes.value.json();
        setGroundwaterData(gwData);
      }

    } catch (error) {
      console.error('Error fetching district data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="district-dashboard">
        <Header 
          isDistrictView={true} 
          districtName={selectedDistrict} 
          stateName={selectedState} 
        />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading {selectedDistrict} District Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="district-dashboard">
      <Header 
        isDistrictView={true} 
        districtName={selectedDistrict} 
        stateName={selectedState} 
      />
      
      {/* Weather Widget */}
      <div style={{ marginTop: '20px' }}>
        <WeatherWidget location={selectedDistrict} isDistrictView={true} />
      </div>

      {/* District Overview Section */}
      <div className="district-overview-section">
        <div className="overview-cards-grid">
          {/* Groundwater Status Card */}
          <div className="overview-card groundwater-enhanced">
            <div className="card-header-enhanced">
              <div className="header-content">
                <h3>💧 Groundwater Analytics</h3>
                <span className={`status-indicator ${(groundwaterData?.category || 'Safe')?.toLowerCase()?.replace(/\s+/g, '-')}`}>
                  {groundwaterData?.category || 'Safe'}
                </span>
              </div>
            </div>
            <div className="card-content-enhanced">
              <div className="gw-metrics-enhanced">
                <div className="gw-metric-enhanced primary">
                  <div className="metric-icon-large">🌧️</div>
                  <div className="metric-content">
                    <span className="metric-value-large">{groundwaterData?.rainfall?.toFixed(1) || '650.0'}</span>
                    <span className="metric-unit">mm</span>
                    <span className="metric-label-enhanced">Annual Rainfall</span>
                  </div>
                </div>
                
                <div className="gw-metrics-row">
                  <div className="gw-metric-enhanced">
                    <span className="metric-label-small">Extractable Resources</span>
                    <span className="metric-value-medium">{groundwaterData ? (groundwaterData.annualExtractableGroundWaterResources / 1000)?.toFixed(1) : '125.5'}K</span>
                    <span className="metric-unit-small">ham</span>
                  </div>
                  <div className="gw-metric-enhanced">
                    <span className="metric-label-small">Current Extraction</span>
                    <span className="metric-value-medium">{groundwaterData ? (groundwaterData.groundWaterExtraction / 1000)?.toFixed(1) : '85.2'}K</span>
                    <span className="metric-unit-small">ham</span>
                  </div>
                </div>
                
                <div className="extraction-ratio-bar">
                  <div className="ratio-header">
                    <span className="ratio-label">Extraction Ratio</span>
                    <span className="ratio-value">
                      {groundwaterData && groundwaterData.annualExtractableGroundWaterResources > 0 
                        ? ((groundwaterData.groundWaterExtraction / groundwaterData.annualExtractableGroundWaterResources) * 100).toFixed(1)
                        : '67.9'}%
                    </span>
                  </div>
                  <div className="ratio-bar">
                    <div 
                      className="ratio-fill"
                      style={{
                        width: `${Math.min(
                          groundwaterData && groundwaterData.annualExtractableGroundWaterResources > 0 
                            ? ((groundwaterData.groundWaterExtraction / groundwaterData.annualExtractableGroundWaterResources) * 100)
                            : 67.9, 
                          100
                        )}%`
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Major Crops Card - Enhanced */}
          <div className="overview-card crops-enhanced">
            <div className="card-header-enhanced">
              <div className="header-content">
                <h3>🌾 Major Crops Production</h3>
                <span className="card-badge">District Crops</span>
              </div>
            </div>
            <div className="card-content-enhanced">
              {loading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div style={{ fontSize: '24px', marginBottom: '10px' }}>🌾</div>
                  <div>Loading crop data...</div>
                </div>
              ) : (
                <div className="crops-list-enhanced">
                  {(() => {
                    // Display district crops if available
                    if (districtData) {
                      const crops = [];
                      
                      // Add primary crop
                      if (districtData.primary && districtData.primaryProd) {
                        crops.push({
                          crop: districtData.primary,
                          production: districtData.primaryProd
                        });
                      }
                      
                      // Add secondary crop
                      if (districtData.secondary && districtData.secondaryProd) {
                        crops.push({
                          crop: districtData.secondary,
                          production: districtData.secondaryProd
                        });
                      }
                      
                      // Sort by production (highest first)
                      crops.sort((a, b) => b.production - a.production);
                      
                      if (crops.length > 0) {
                        return crops.map(({ crop, production }, index) => {
                          const displayValue = (production / 1000).toFixed(1);
                          
                          return (
                            <div key={crop} className="crop-item-enhanced">
                              <div className="crop-rank">{index + 1}</div>
                              <div className="crop-details">
                                <div className="crop-name">{crop}</div>
                                <div className="crop-production">
                                  <span className="production-value">{displayValue}</span>
                                  <span className="production-unit">K MT</span>
                                </div>
                              </div>
                              <div className="crop-trend">
                                <span className="trend-indicator positive">↗</span>
                              </div>
                            </div>
                          );
                        });
                      }
                    }
                    
                    // Fallback dummy data if no district data available
                    const dummyCrops = [
                      { crop: 'Wheat', production: 1500 },
                      { crop: 'Rice', production: 1200 }
                    ];
                    
                    return dummyCrops.map(({ crop, production }, index) => {
                      const displayValue = (production / 1000).toFixed(1);
                      
                      return (
                        <div key={crop} className="crop-item-enhanced">
                          <div className="crop-rank">{index + 1}</div>
                          <div className="crop-details">
                            <div className="crop-name">{crop}</div>
                            <div className="crop-production">
                              <span className="production-value">{displayValue}</span>
                              <span className="production-unit">K MT</span>
                            </div>
                          </div>
                          <div className="crop-trend">
                            <span className="trend-indicator positive">↗</span>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* Seasonal Progress Card - New */}
          <div className="overview-card seasonal-enhanced">
            <div className="card-header-enhanced">
              <div className="header-content">
                <h3>🗓️ {seasonalData?.currentSeason || 'Rabi'} Season Status</h3>
                <span className="season-badge">{seasonalData?.currentSeason || 'Rabi'}</span>
              </div>
            </div>
            <div className="card-content-enhanced">
              <div className="seasonal-metrics">
                <div className="seasonal-progress">
                  <div className="progress-item">
                    <span className="progress-label">Sowing Progress</span>
                    <div className="progress-bar-container">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill sowing"
                          style={{ width: `${seasonalData?.sowingProgress || 75}%` }}
                        ></div>
                      </div>
                      <span className="progress-value">{seasonalData?.sowingProgress || 75}%</span>
                    </div>
                  </div>
                  {(seasonalData?.harvestProgress || 0) > 0 && (
                    <div className="progress-item">
                      <span className="progress-label">Harvest Progress</span>
                      <div className="progress-bar-container">
                        <div className="progress-bar">
                          <div 
                            className="progress-fill harvest"
                            style={{ width: `${seasonalData.harvestProgress}%` }}
                          ></div>
                        </div>
                        <span className="progress-value">{seasonalData.harvestProgress}%</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="seasonal-stats">
                  <div className="seasonal-stat">
                    <span className="stat-icon">🎯</span>
                    <span className="stat-value">{seasonalData?.expectedYield || 92}%</span>
                    <span className="stat-label">Expected Yield</span>
                  </div>
                  <div className="seasonal-stat">
                    <span className="stat-icon">🌤️</span>
                    <span className="stat-value">{seasonalData?.weatherSuitability || 85}%</span>
                    <span className="stat-label">Weather Suitability</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Infrastructure & Support Card - New */}
          <div className="overview-card infrastructure-enhanced">
            <div className="card-header-enhanced">
              <div className="header-content">
                <h3>🏗️ Infrastructure & Support</h3>
                <span className="card-badge">Resources</span>
              </div>
            </div>
            <div className="card-content-enhanced">
              <div className="infrastructure-grid">
                <div className="infra-item">
                  <div className="infra-icon">💧</div>
                  <div className="infra-content">
                    <span className="infra-value">{agriculturalMetrics?.irrigationCoverage || 72}%</span>
                    <span className="infra-label">Irrigation Coverage</span>
                  </div>
                  <div className="infra-trend positive">↗</div>
                </div>
                <div className="infra-item">
                  <div className="infra-icon">🌱</div>
                  <div className="infra-content">
                    <span className="infra-value">{agriculturalMetrics?.organicFarming || 12}%</span>
                    <span className="infra-label">Organic Farming</span>
                  </div>
                  <div className="infra-trend positive">↗</div>
                </div>
                <div className="infra-item">
                  <div className="infra-icon">🤝</div>
                  <div className="infra-content">
                    <span className="infra-value">{agriculturalMetrics?.cooperatives || 28}</span>
                    <span className="infra-label">Cooperatives</span>
                  </div>
                  <div className="infra-trend stable">→</div>
                </div>
                <div className="infra-item">
                  <div className="infra-icon">💰</div>
                  <div className="infra-content">
                    <span className="infra-value">₹{Math.round((agriculturalMetrics?.marketPrice || 4200) / 100)}</span>
                    <span className="infra-label">Avg Market Price</span>
                  </div>
                  <div className="infra-trend positive">↗</div>
                </div>
              </div>
              <div className="soil-health-indicator">
                <span className="soil-label">Soil Health Index:</span>
                <span className={`soil-status ${(agriculturalMetrics?.soilHealth || 'Good').toLowerCase()}`}>
                  {agriculturalMetrics?.soilHealth || 'Good'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* India Map with highlighted district */}
      <IndiaMap 
        isDistrictView={true}
        selectedState={selectedState}
        selectedDistrict={selectedDistrict}
        highlightState={selectedState}
        highlightDistrict={selectedDistrict}
      />
      
      <AgricultureSummary 
        isDistrictView={true} 
        districtName={selectedDistrict} 
        stateName={selectedState} 
      />
      <NotificationPopup />
      <Footer />
    </div>
  );
};

export default DistrictDashboard;